import { useContext } from 'react';
import { useHistory } from 'react-router-dom';

import { store } from '../../store';
import { paths } from '../../routes/Root/paths';
import { IAlert, IMap, ICoordXYXY } from '../../models/types';
import { getAreaBounds } from '../../routes/Main/utils';
import { readGboxCorners } from '../../utils/planCorners.utils';
import { isMapEditorRoute } from './utils';
import { ToastContext } from '../../components_/Toast';

// Достаточно навигационного контракта react-router v5; полный тип History
// тянуть из пакета history не требуется
interface INavigation {
  location: { pathname: string };
  push: (path: string) => void;
}

interface IFocusAlertOptions {
  // Ручной переход по ссылке «Перейти к месту» в тосте — работает с любой страницы.
  // Без флага (автозапуск по приходу уведомления) срабатывает только в разделе «Карта».
  manual?: boolean;
}

// Поколение фокуса: пока грузятся офис/план одного уведомления, может прийти
// другое — обзор применяет только самое позднее, иначе медленный старый
// ответ перебивал бы фокус нового события
let focusGeneration = 0;

// «Пустой» bbox: нули или вырожденный (нет ширины/высоты) — подгонка по нему
// дала бы maxZoom в одну точку (типичный случай — псевдоплан «Все планы»)
const bboxIsEmpty = (bbox: number[]): boolean =>
  bbox.length !== 4 || bbox.every(coord => !coord) || bbox[0] === bbox[2] || bbox[1] === bbox[3];

// Границы плана для подгонки обзора в текущем режиме: в режиме плана — bbox
// (локальные координаты изображения), в гео-режиме — углы gbox (план
// отрисован геопривязанным на подложке). null — границ нет.
const getPlanFitBounds = (map: IMap, isGeo: boolean): ICoordXYXY | null => {
  if (!isGeo) {
    if (!map.bbox || bboxIsEmpty(map.bbox)) {
      return null;
    }

    return [
      [map.bbox[1], map.bbox[0]],
      [map.bbox[3], map.bbox[2]]
    ];
  }

  const corners = readGboxCorners(map.gbox);

  if (!corners) {
    return null;
  }

  const lats = corners.map(corner => corner.lat);
  const lngs = corners.map(corner => corner.lng);

  return [
    [Math.min(...lats), Math.min(...lngs)],
    [Math.max(...lats), Math.max(...lngs)]
  ];
};

export const focusMapOnAlert = (alert: IAlert) => {
  const d = store.dispatch as any;
  const { map, layers } = store.getState().infrastructureMap;
  const { isGeo } = layers;

  const area = alert.areaId
    ? (map.areas || []).find(item => String(item.id) === String(alert.areaId))
    : null;

  if (area) {
    d.infrastructureAreas.setAlertArea(area);
    // Видимость зоны независимо от фильтров слоёв обеспечивает AreaList —
    // выбор оператора в фильтрах здесь не трогаем

    // Границы берём в координатах текущего режима: гео — geomGlobalCoords,
    // режим плана — geomCoords
    const bounds = getAreaBounds(area, isGeo);
    if (bounds) {
      d.infrastructureMap.requestFit({ bounds });
      return;
    }
  }

  // У нового события зоны нет (или её нет на плане) — гасим подсветку прежней
  d.infrastructureAreas.resetAlertArea();

  // Зоны нет (или её границы не вычислить) — показываем план целиком
  const planBounds = getPlanFitBounds(map, isGeo);
  if (planBounds) {
    d.infrastructureMap.requestFit({ bounds: planBounds });
    return;
  }

  // Плана с границами нет (например, «Все планы») — прежнее поведение по координатам
  const lat = (alert as any).lat;
  const lon = (alert as any).lon;
  if (typeof lat === 'number' && typeof lon === 'number') {
    d.infrastructureMap.setFocus({ center: [lat, lon], zoom: 17 });
  }
};

const focusAlertTarget = async (
  alert: IAlert,
  history: INavigation,
  toast: ((params: { text: string; type?: string }) => void) | null,
  options?: IFocusAlertOptions
) => {
  const manual = Boolean(options && options.manual);
  const { pathname } = history.location;

  // Автопереход — только пока оператор в разделе «Карта» и не в форме
  // редактирования: getOffice/getMap подменяют записи, к которым привязана
  // открытая форма, и сохранение ушло бы не туда (не тот офис/план)
  if (!manual && (!pathname.startsWith(paths.map.root) || isMapEditorRoute(pathname))) {
    return;
  }

  try {
    const officeId = (alert as any).officeId ?? (alert as any).placeId;
    const mapId = (alert as any).mapId;

    if (!mapId && !officeId) {
      return;
    }

    const generation = ++focusGeneration;

    // localStorage пишем синхронно до перехода: OfficeRoute читает mapId при первом рендере
    if (officeId) {
      localStorage.setItem('officeId', String(officeId));
    }
    if (mapId) {
      localStorage.setItem('mapId', String(mapId));
    } else if (officeId) {
      localStorage.setItem('mapId', 'all');
    }

    // Уходим с маршрутов без вида плана до переключения режимов: со списка
    // офисов — иначе force-geo вернёт isGeo; с формы редактирования — иначе
    // она молча подменится под оператором (ручной переход с формы)
    if (!pathname.startsWith(paths.map.office.root) || isMapEditorRoute(pathname)) {
      history.push(paths.map.office.root);
    }

    // Автофокус живёт, пока оператор в разделе карты и не открыл форму
    const bailed = () => {
      const current = history.location.pathname;

      return !current.startsWith(paths.map.root) || isMapEditorRoute(current);
    };

    if (officeId) {
      await store.dispatch.infrastructureOffices.getOffice(officeId);
    }

    // До смены режима и плана: оператор мог открыть форму, пока грузился офис
    if (!manual && (bailed() || generation !== focusGeneration)) {
      return;
    }

    if (mapId) {
      // Режим просмотра сохраняем текущий: в гео план откроется
      // геопривязанным на подложке, в режиме плана — на весь экран
      await store.dispatch.infrastructureMap.getMap(mapId);
    } else if (officeId) {
      await store.dispatch.infrastructureMap.getMap('all' as any);
    }

    // Медленный старый фокус не должен перебивать новый; и не двигаем обзор,
    // если оператор за время загрузки ушёл из карты или открыл форму
    if (generation !== focusGeneration || (!manual && bailed())) {
      return;
    }

    focusMapOnAlert(alert);
  } catch (error) {
    // tslint:disable-next-line:no-console
    console.error('Alert map focus failed', error);

    // Ручной переход: оператор кликнул ссылку и должен увидеть, что она не сработала
    if (manual && toast) {
      toast({ text: 'Не удалось перейти к месту события.', type: 'error' });
    }
  }
};

export const useAlertNavigation = () => {
  const history = useHistory();
  const toast = useContext(ToastContext);

  return (alert: IAlert, options?: IFocusAlertOptions) =>
    focusAlertTarget(alert, history, toast, options);
};
